/* eslint-disable no-prototype-builtins */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import 'ag-grid-enterprise';
import {
    createStyles,
    WithStyles,
    withStyles
} from '@mui/styles';
import { Theme } from '@mui/material/styles';
import {
    inject,
    observer
} from 'mobx-react';
import * as React from 'react';
import { RootStore } from '../../stores/root.store';
import { SideNavbar } from '../../shared/components/side-navbar';
import ArticleIcon from '@mui/icons-material/Article';
import {
    Divider,
    Typography,
    TextField,
    Autocomplete,
    Paper,
    Button
} from '@mui/material';
import { CCSnackbar, CCSnackbarVariant } from '../../shared/components/cc-snackbar';
import { SimpleDialog } from '../../shared/components/simple-dialog';
import { AssignRouteWizard } from '../../components/assign-route-wizard';
import { SearchResultsUser } from '../../adapters/report-base-adapter';
import {
    EntityPCN,
    PCNAssignmentUser,
    PCNList,
    ScheduleRow,
    SessionTypeParameters,
    UserTeam
} from '../../shared/domain';
import { CCSpinner } from '../../shared/components/cc-spinner';
import { ValidGridRefData } from '../../shared/components/ag-grid-list';
import { ColDef } from 'ag-grid-community';
import { SimpleListItem } from '../../shared/components/simple-list';
import { PageBoundary, SimpleListPagination } from '../../shared/components/simple-list-pagination';
import { PCN_ROUTES } from '../../constants';
import memoize from 'memoize-one';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Print } from '@mui/icons-material';
import saveCsv from 'save-csv';
import { CCDatePickerButton, CCDatePickerDateRange } from '../../components/cc-date-picker';

dayjs.extend(timezone);
dayjs.extend(utc);

const CHARACTER_NUMBER = 2;
const DEBOUNCE_TIME = 500;
const ASSIGNMENT_STATUS_FILTER = [
    {
        value: 'assigned',
        label: 'Assigned'
    },
    {
        value: 'unassigned',
        label: 'Unassigned'
    }
];

const DATEFORMAT = 'YYYY-MM-DDTHH:mm:ss';
// Set default for routes prefix
let CC_ROUTES_NAME: string | undefined = undefined;

const styles = (theme: Theme) => createStyles({
    divider: {
        margin: '1.6em 0 1.55em 0',
    },
    contentContainer: {
        '&, &$navbarContent': {
            padding: '1.45em',
        },
        [theme.breakpoints.between(
            'xs',
            theme.breakpoints.values.iPhone8_Landscape
        )]: {
            padding: '0',
        },
    },
    errorMessage: {
        width: '100%',
        color: 'red',
        fontWeight: 400,
        textAlign: 'center',
        margin: '1em'
    },
    navbarIconContainer: {
        padding: '0.3em 0.4em',
        backgroundColor: theme.palette.secondary.main,
    },
    navbarIconContainerSmall: {
        transform: 'scale(0.7)',
        margin: '0.7em',
    },
    navbarIcon: {
        color: 'white',
    },
    navbarContent: {
        display: 'flex',
        flex: '1 0 0',
        flexDirection: 'column',
        width: '20em',
    },
    navbarTitleContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    navbarTitle: {
        fontWeight: 'bold',
        marginLeft: '0.4em',
    },
    bodyTitle: {
        marginBottom: '0.4em',
    },
    modalContainer: {
        padding: '2em',
        maxWidth: '55em',
        width: '100%'
    },
    modalHeaderContainer: {
        position: 'relative',
        padding: '8px'
    },
    modalTitle: {
        fontSize: '2em',
        fontWeight: 'bold',
        marginBottom: '0.5em'
    },
    navbarComponentTitle: {
        color: theme.palette.grey[500],
        fontSize: '0.9em',
        fontWeight: 600,
        marginBottom: '0.3em',
    },
    modalCloseButton: {
        position: 'absolute',
        right: '0',
        top: '0'
    },
    modalSectionContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap'
    },
    modalBodyList: {
        padding: '0',
        listStyle: 'none',
        minWidth: '14em',
        '& li': {
            padding: '0.9em 0'
        },
        '& span': {
            fontWeight: 'bold',
            marginRight: '0.5em'
        }
    },
    bodyLeftColumn: {
        width: '32em'
    },
    headerColumn: {
        padding: '0.3em 0',
        width: '22em'
    },
    moduleGroupField: {
        width: '100%',
        minWidth: '14.3em'
    },
    snackbar: {
        top: '7em',
    },
    navbarInput: {
        width: '100%',
        paddingLeft: '0.4em',
    },
    navbarPlaceholder: {
        fontSize: '0.9em',
    },
    addRouteButton: {
        width: '100%',
        height: '4em',
        marginBottom: '1em',
        '& .MuiSvgIcon-root': {
            marginRight: '0.2em',
            transform: 'scale(0.9)'
        }
    },
    assignmentsContainer: {
        padding: '2em',
        flex: '1'
    },
    spinner: {
        zIndex: 1301,
        position: 'fixed',
    },
    tasksContainerSize: {
        flex: 1,
        '& .MuiListItem-root': {
            padding: '0.5em !important'
        }
    },
    topMargin: {
        marginTop: '2em'
    },
    editCell: {
        paddingLeft: '4px',
        '& .MuiSvgIcon-root': {
            position: 'relative',
            top: '0.25em',
            left: '0.2em'
        }
    },
    pcnList: {
        padding: '1em',
        minHeight: '80vh'
    },
    inputContainer: {
        display: 'flex',
        flexDirection: 'column',
        marginTop: '1.6em',
    },
    routeNavButton: {
        width: '100%',
        height: '4em',
        marginBottom: '1em',
        '& .MuiSvgIcon-root': {
            marginRight: '0.2em',
            transform: 'scale(0.9)'
        }
    },
});

interface SchedulingRoutesDic {
    portal3_tab: {
        default_grid_values: { [key: string]: string };
        route_columns_definition: ColDef[];
        default_pcl_team_id: string;
        routes_prefix?: string;
        version: number;
    };
}

interface changeModulePermission {
    data: { change_module_group_permission: boolean };
    status: boolean;
}

interface FolderFilter {
    id: string;
    name: string;
}

interface StatusFilter {
    value: string,
    label: string
}

interface Props extends WithStyles<typeof styles> {
    rootStore: RootStore;
    userTeam?: UserTeam;
}

interface States {
    errorMessage: string;
    totalRows: number;
    isAssignmentsDrawerOpen: boolean;
    snackbarVariant: CCSnackbarVariant;
    snackbarMessage: string;
    snackbarOpen: boolean;
    dialogOpen: boolean;
    dialogTitle: string;
    dialogMessage: string;
    validClipboardRefData: ValidGridRefData;
    isCreateRouteGridReady: boolean;
    isAssignmentWizardOpen: boolean;
    showUpdateCategory: boolean;
    currentFolder: string;
    pcnUsers: PCNAssignmentUser[];
    userListId: string;
    editModeGrid: boolean;
    isRouteEdit: boolean;
    editRouteRows: ScheduleRow[];
    selectedPcn: EntityPCN | undefined;
    currentPcn: string;
    isRouteReadyModal: boolean;
    filterPcnValue: string;
    pcnToFilter: PCNList | undefined;
    filterFolderValue: string;
    folderToFilter: FolderFilter | undefined;
    userToFilter: SearchResultsUser | undefined;
    filterEmailValue: string;
    isFilterLoading: boolean;
    pcnFilters: PCNList[];
    folderFilters: FolderFilter[];
    pcnOffset: number;
    pcnPage: number;
    teamUsers: SearchResultsUser[];
    statusFilter: StatusFilter | undefined;
    statusFilterValue: string;
    sessionTypes: FolderFilter[];
    sessionToFilter: FolderFilter | undefined;
    sessionFilterValue: string;
    isFilterFolderOpen: boolean;
    isFilterPcnOpen: boolean;
    isFilterEmailOpen: boolean;
    startDate: string | undefined;
    endDate: string | undefined;
}

@inject('rootStore')
@observer
class AssignmentsTab extends React.Component<Props, States> {
    public static defaultProps = {
    };
    
    state: States = {
        errorMessage: '',
        totalRows: 0,
        isAssignmentsDrawerOpen: true,
        snackbarVariant: 'info' as CCSnackbarVariant,
        snackbarMessage: '',
        snackbarOpen: false,
        dialogOpen: false,
        dialogTitle: '',
        dialogMessage: '',
        validClipboardRefData: {},
        isCreateRouteGridReady: false,
        isAssignmentWizardOpen: false,
        showUpdateCategory: false,
        currentFolder: '',
        pcnUsers: [] as PCNAssignmentUser[],
        userListId: '',
        editModeGrid: true,
        isRouteEdit: false,
        editRouteRows: [],
        selectedPcn: undefined as EntityPCN | undefined,
        currentPcn: '',
        isRouteReadyModal: false,
        filterPcnValue: '',
        pcnToFilter: undefined as PCNList | undefined,
        filterFolderValue: '',
        folderToFilter: undefined as FolderFilter | undefined,
        userToFilter: undefined as SearchResultsUser | undefined,
        filterEmailValue: '',
        isFilterLoading: false,
        pcnFilters: [] as PCNList[],
        folderFilters: [] as FolderFilter[],
        pcnOffset: 0,
        pcnPage: 0,
        teamUsers: [] as SearchResultsUser[],
        statusFilter: undefined as StatusFilter | undefined,
        statusFilterValue: '',
        sessionTypes: [] as FolderFilter[],
        sessionToFilter: undefined as FolderFilter | undefined,
        sessionFilterValue: '',
        isFilterFolderOpen: false,
        isFilterPcnOpen: false,
        isFilterEmailOpen: false,
        startDate: undefined,
        endDate: undefined,
    };

    defaultGridValues: {[key: string]: string} = {};
    defaultPCLTeamId = '';
    currentTimezone = dayjs.tz.guess();
    debounceTimeout?: NodeJS.Timeout = undefined;
    isNewVersion = false;

    componentDidMount() {
        const {
            rootStore: { managerStore },
            userTeam,
        } = this.props;
        const {
            error,
            currentAccount,
            getSessionTypes
        } = managerStore;

        const schedulingRoutes = currentAccount?.schedulingRoutes as SchedulingRoutesDic | undefined;
        const portal3Tab = schedulingRoutes?.portal3_tab;
        
        if (portal3Tab) {
            this.defaultGridValues = portal3Tab.default_grid_values;
            this.defaultPCLTeamId = portal3Tab.default_pcl_team_id;
            CC_ROUTES_NAME = portal3Tab?.routes_prefix;
            this.isNewVersion = portal3Tab?.version === 3;

            if (this.isNewVersion) {
                getSessionTypes().then((sessionTypes: SessionTypeParameters[]) => {
                    if (!sessionTypes.length) {
                        this.showWarning('There is no SessionTypes assigned to this account');
                        return;
                    }

                    this.setState({
                        sessionTypes: sessionTypes.map((session) => {
                            return {
                                id: `${session.id}`,
                                name: session.name
                            };
                        })
                    });
                });
            }

            this.updatePCNList();
        }

        if (userTeam) {
            managerStore
                .checkChangeCategoryByTeam(userTeam.teamId)
                .then((response) => {
                    const { data } =
                        response as unknown as changeModulePermission;
                    if (data.change_module_group_permission) {
                        this.setState({ showUpdateCategory: true });
                    } else {
                        this.setState({ showUpdateCategory: false });
                    }
                })
                .catch((error) => {
                    this.showError(error);
                });
        }

        if (error) {
            this.showError(error);
            managerStore.error = '';
        }
    }

    showSnackbar = (
        snackbarMessage: string,
        snackbarVariant: CCSnackbarVariant
    ) => {
        this.setState({
            snackbarVariant,
            snackbarMessage,
            snackbarOpen: true,
        });
    };

    showError = (errorMessage: string | undefined) => {
        let message;
        if (errorMessage) {
            message = errorMessage;
        } else {
            message = 'Unknown Error';
        }

        this.showSnackbar(message, 'error' as CCSnackbarVariant);
    };

    showWarning = (warningMessage: string | undefined) => {
        if (!warningMessage) {
            return;
        }

        this.showSnackbar(warningMessage, 'warning' as CCSnackbarVariant);
    };

    showDialog = (dialogTitle: string, dialogMessage: string) => {
        this.setState({
            dialogTitle,
            dialogMessage,
            dialogOpen: true
        });
    };

    onSnackbarClosed = () => {
        this.setState({ snackbarOpen: false });
    };

    onDialogClosed = () => {
        this.setState({ dialogOpen: false });
    };

    onToggleSideNavBar = () => {
        const { isAssignmentsDrawerOpen } = this.state;
        this.setState({ isAssignmentsDrawerOpen: !isAssignmentsDrawerOpen});
    };

    onWizardSubmit = (selectedUsers: SearchResultsUser[], startDate: Date, endDate?: Date | undefined) => {
        const {
            rootStore: { 
                managerStore
            },
            userTeam
        } = this.props;
        const { selectedPcn } = this.state;
        const { pcnId } = selectedPcn as EntityPCN;
        const secondsInDay = 86400;
        const startDateFormated = this.formatUTCUploadDate(startDate).unix();
        // Add 1 day to cover the entire day
        const endDateFormated = !endDate ? undefined : this.formatUTCUploadDate(endDate).unix() + secondsInDay;

        // // Check if there is a selected users to include into list
        if (selectedUsers.length) {
            const selectedUser = selectedUsers[0];
            const userId = selectedUser.user_id;
            // Edit and include the new users
            managerStore.addUserAssignment(
                Number(pcnId),
                userId,
                startDateFormated,
                endDateFormated,
                this.currentTimezone,
                userTeam
            ).then((message) => {
                this.updatePCNList();
                this.showSnackbar(
                    message,
                    'success' as CCSnackbarVariant
                );
            }).catch((error) => {
                this.showError(error);
            });
        }
        // Close modal
        this.onCancelWizard();
    };

    onCancelWizard = () => {
        this.setState({
            isAssignmentWizardOpen: false,
            teamUsers: [] as SearchResultsUser[]
        });
    };

    convertPCNRoutesIntoListItems = memoize(
        (rows: PCNList[]): SimpleListItem[] => {
            return rows.map((item: PCNList) => {
                const {
                    folder_doc_id,
                    id,
                    value,
                    shift_label,
                    folder_name,
                    email,
                    start_time,
                    end_time,
                    session_type_name,
                    session_type_default_assignment_type
                } = item;
                const primaryLabel = `pcn_${folder_doc_id}_${id}`;
                const startDateFormated = start_time ? dayjs(start_time).format('ll') : undefined;
                const endDateFormated = end_time ? dayjs(end_time).format('ll') : undefined;
                const userInfo = email && startDateFormated ? 
                    <>{email}<br/>From {startDateFormated} {endDateFormated ? `to ${endDateFormated}` : ''}</> : '';
                const secondaryLabel = <>
                    {folder_name}{session_type_name !== null ? ` - ${session_type_name}` : ''}{shift_label !== null ? ` - ${shift_label}` : ''}
                    <br/>{userInfo}
                </>;
                const isOnDemand = session_type_default_assignment_type && session_type_default_assignment_type === 'on_demand' ? true : false;
                const data = { pcnId: id, pcnValue: value, shiftLabel: shift_label, folderName: folder_name, disabled: isOnDemand };
                return new SimpleListItem(primaryLabel, value, secondaryLabel, undefined, undefined, undefined, data);
            });
        }
    );


    updatePCNList = () => {
        const {
            rootStore: { 
                managerStore
            },
            userTeam
        } = this.props;

        const {
            pcnOffset,
            statusFilter,
            folderToFilter,
            userToFilter,
            pcnToFilter,
            sessionToFilter,
            startDate,
            endDate
        } = this.state;
        const { PCN_ROUTES_RECORD_LIMIT } = PCN_ROUTES;
        const filters = {
            value: pcnToFilter?.value,
            folder_id: folderToFilter?.id,
            filter_assign: statusFilter?.value,
            user_id: userToFilter?.user_id,
            session_type_id: sessionToFilter?.id,
            start_date: startDate,
            end_date: endDate
        };

        managerStore.getPCNList(PCN_ROUTES_RECORD_LIMIT, pcnOffset, filters, userTeam).catch((error) => {
            this.showError(error);
        });
    };

    setPcnValue = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pcn = `${event.target.value}`.replace('@', '');
        this.setState({ currentPcn: pcn });
    };

    openRouteWizard = () => {
        this.setState({ isAssignmentWizardOpen: true });
    };

    selectPCN = (selectedItem: SimpleListItem) => {
        const {
            rootStore: { 
                managerStore
            },
            userTeam
        } = this.props;

        const {
            label,
            data
        } = selectedItem;

        const pcnId = data?.pcnId as string;
        const folderName = data?.folderName as string;
        const selectedPcn: EntityPCN = {
            pcnId: pcnId,
            pcnValue: data?.pcnValue as string,
            shiftLabel: data?.shiftLabel as string
        };

        managerStore.getPcnUserList(pcnId, true, userTeam).then((users) => {
            this.setState({
                currentPcn: label,
                selectedPcn,
                currentFolder: folderName ? folderName : '',
                isAssignmentWizardOpen: true,
                pcnUsers: users,
                teamUsers: [] as SearchResultsUser[]
            });

        }).catch((error) => {
            this.showError(error);
        });
    };

    onPcnFilterInputChange = (event: React.ChangeEvent<object>, selectedPCN: PCNList | null) => {
        this.setState({
            pcnToFilter: selectedPCN ? selectedPCN : undefined,
            pcnOffset: 0,
            isFilterPcnOpen: false
        }, () => {
            this.updatePCNList();
        });
    };

    onFolderFilterInputChange = (event: React.ChangeEvent<object>, selectedFolder: FolderFilter | null) => {
        this.setState({
            folderToFilter: selectedFolder ? selectedFolder : undefined,
            pcnOffset: 0,
            isFilterFolderOpen: false
        }, () => {
            this.updatePCNList();
        });
    };

    onEmailFilterInputChange = (event: React.ChangeEvent<object>, selectedUser: SearchResultsUser | null) => {
        this.setState({
            userToFilter: selectedUser ? selectedUser : undefined,
            pcnOffset: 0,
            isFilterEmailOpen: false
        }, () => {
            this.updatePCNList();
        });
    };

    onStatusFilterInputChange = (event: React.ChangeEvent<object>, selectedStatus: StatusFilter | null) => {
        this.setState({
            statusFilter: selectedStatus ? selectedStatus : undefined,
            pcnOffset: 0
        }, () => {
            this.updatePCNList();
        });
    };

    onSessionTypeFilterInputChange = (event: React.ChangeEvent<object>, selectedSession: FolderFilter | null) => {
        this.setState({
            sessionToFilter: selectedSession ? selectedSession : undefined,
            pcnOffset: 0
        }, () => {
            this.updatePCNList();
        });
    };

    setFilterPcnValue = (event: React.SyntheticEvent<Element, Event>, value: string) => {
        this.setState({
            filterPcnValue: value
        }, () => {
            if (this.debounceTimeout) {
                clearTimeout(this.debounceTimeout as NodeJS.Timeout);
            }

            if (value.length > CHARACTER_NUMBER) {
                this.debounceTimeout = setTimeout(() => {
                    const { pcnToFilter } = this.state;
                    if (!pcnToFilter) {
                        this.uploadFilterPcnList(value);
                    }
                }, DEBOUNCE_TIME);
            } else {
                this.setState({
                    pcnFilters: [] as PCNList[],
                    isFilterPcnOpen: false
                });
            }
        });

    };

    setFilterStatusValue = (event: React.SyntheticEvent<Element, Event>, value: string) => {
        this.setState({ statusFilterValue: value });
    };

    setFilterSessionValue = (event: React.SyntheticEvent<Element, Event>, value: string) => {
        this.setState({ sessionFilterValue: value });
    };

    uploadFilterPcnList = (value: string) => {
        const {
            rootStore: { 
                managerStore
            },
            userTeam
        } = this.props;

        managerStore.getPCNFilterList({ value }, userTeam).then(({ rows }) => {
            this.setState({ pcnFilters: rows, isFilterPcnOpen: true });
        }).catch((error) => {
            this.showError(error);
        });
    };

    setFilterFolderValue = (event: React.SyntheticEvent<Element, Event>, value: string) => {
        this.setState({
            filterFolderValue: value
        }, () => {
            if (this.debounceTimeout) {
                clearTimeout(this.debounceTimeout as NodeJS.Timeout);
            }

            if (value.length > CHARACTER_NUMBER) {
                this.debounceTimeout = setTimeout(() => {
                    const { folderToFilter } = this.state;
                    if (!folderToFilter) {
                        this.uploadFolderFilterList(value);
                    }
                }, DEBOUNCE_TIME);
            } else {
                this.setState({
                    folderFilters: [] as FolderFilter[],
                    isFilterFolderOpen: false
                });
            }
        });
    };

    uploadFolderFilterList = (value: string) => {
        const {
            rootStore: { 
                managerStore
            },
            userTeam
        } = this.props;

        managerStore.getPCNFilterList({folder_name: value}, userTeam).then(({ rows }) => {
            const folderFilters: FolderFilter[] = [];
            rows.map( (item: PCNList) => {
                const { folder_doc_id, folder_name } = item;
                const isNewFolder = folderFilters.filter((item) => item.id === folder_doc_id);
                if (!isNewFolder.length) {
                    folderFilters.push({
                        id: folder_doc_id,
                        name: folder_name
                    });
                }
            });

            this.setState({ folderFilters: folderFilters, isFilterFolderOpen: true });
        }).catch((error) => {
            this.showError(error);
        });
    };

    setFilterEmailValue = (event: React.SyntheticEvent<Element, Event>, email: string) => {
        this.setState({
            filterEmailValue: email
        }, () => {
            if (this.debounceTimeout) {
                clearTimeout(this.debounceTimeout as NodeJS.Timeout);
            }

            if (email.length > CHARACTER_NUMBER) {
                this.debounceTimeout = setTimeout(() => {
                    const { userToFilter } = this.state;
                    if (!userToFilter) {
                        this.uploadEmailFilterList(email);
                    }
                }, DEBOUNCE_TIME);
            } else {
                this.setState({
                    teamUsers: [] as SearchResultsUser[],
                    isFilterEmailOpen: false
                });
            }
        });
    };

    uploadEmailFilterList = (email: string) => {
        const {
            rootStore: { 
                managerStore
            },
            userTeam
        } = this.props;

        managerStore.getTeamUsers(this.defaultPCLTeamId, email, userTeam).then((users: SearchResultsUser[]) => {
            this.setState({ teamUsers: users, isFilterEmailOpen: true });
        }).catch((error) => {
            this.showError(error);
        });
    };

    onPCNBufferOverBoundary = (
        boundary: PageBoundary,
        nextPage: number
    ): Promise<void> => {
        return new Promise((resolve) => {
            const {
                rootStore: {
                    managerStore: {
                        pcnRoutesTotal
                    }
                }
            } = this.props;

            const { pcnOffset: currentOffset } = this.state;

            const { PCN_ROUTES_RECORD_LIMIT, PCN_ROUTES_MAX_ROWS } = PCN_ROUTES;

            const isFirstPage = nextPage === 0;
            const isLastPage =
                nextPage ===
                Math.ceil(pcnRoutesTotal / PCN_ROUTES_MAX_ROWS) - 1;
            let newOffset = isFirstPage
                ? 0
                : isLastPage
                    ? PCN_ROUTES_RECORD_LIMIT * Math.floor(pcnRoutesTotal / PCN_ROUTES_RECORD_LIMIT)
                    : boundary === PageBoundary.Upper
                        ? currentOffset + PCN_ROUTES_RECORD_LIMIT
                        : currentOffset - PCN_ROUTES_RECORD_LIMIT;

            // If we reached exactly the end of the buffer we go one page back
            if (isLastPage && newOffset === pcnRoutesTotal) {
                newOffset -= PCN_ROUTES_RECORD_LIMIT;
            }

            this.setState({
                pcnOffset: newOffset,
                pcnPage: nextPage,
            }, () => {
                
                this.updatePCNList();
            });

            resolve();
        });
    };

    onFilterUserByEmail = (email: string) => {
        const {
            rootStore: { managerStore },
            userTeam
        } = this.props;

        if (email === '') {
            this.setState({ teamUsers: [] as SearchResultsUser[] });
        } else {
            managerStore.getTeamUsers(this.defaultPCLTeamId, email, userTeam).then((users: SearchResultsUser[]) => {
                this.setState({ teamUsers: users });
            }).catch((error) => {
                this.showError(error);
            });
        }
    };

    formatUTCUploadDate = (date: Date) => {
        const sourceDateTime = dayjs.tz(date, this.currentTimezone);
        return sourceDateTime.utc();
    };

    escapeForCSV = (value: string): string => {
        // Check if the string contains a comma, newline, or double quote
        
        if (/[,"\n/]/.test(value)) {
            // Escape any double quotes by doubling them
            const escapedValue = value.replace(/"/g, '""');
            // Enclose the value in double quotes
            return `"${escapedValue}"`;
        }
        return value;
    };

    exportToCSV = () => {
        const {
            rootStore: {
                managerStore
            },
            userTeam
        } = this.props;
        const { pcnToFilter, folderToFilter, statusFilter } = this.state;
        const routeTitle = CC_ROUTES_NAME ? `${CC_ROUTES_NAME} ROUTE` : 'ROUTE';

        managerStore.getPCNListToExport(
            {
                value: pcnToFilter?.value,
                folder_id: folderToFilter?.id,
                filter_assign: statusFilter?.value
            },
            userTeam
        ).then((response) => {
            const { rows } = response;
            const csvData = rows.map(item => {
                return {
                    'SCHOOL': this.escapeForCSV(item.folder_name),
                    [routeTitle]: this.escapeForCSV(item.value),
                    'SHIFT LABEL': item.shift_label,
                    'MARKER COUNT': item.marker_count,
                    'STATUS': item.email ? item.email : 'UNASSIGNED',
                    'ASSIGNMENT START TIME': item.start_time ? item.start_time : 'NULL',
                    'ASSIGNMENT END TIME': item.end_time ? item.end_time : 'NULL'
                };
            });
    
            const currentDate = new Date();
            const monthRaw = `${currentDate.getMonth() + 1}`;
            const dayRaw = currentDate.getDate();
            const formattedTime = currentDate.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            });
            const month =
                monthRaw.length < 2 ? `0${monthRaw}` : monthRaw;
            let selectedFilters = '-';
            if (pcnToFilter) {
                selectedFilters += `${pcnToFilter.value}-`;
            }
            if (folderToFilter) {
                selectedFilters += `${folderToFilter.name}-`;
            }
            if (statusFilter) {
                selectedFilters += `${statusFilter.label}-`;
            }
            const filename = `assignments${selectedFilters}-${month}-${dayRaw}-${currentDate.getFullYear()}-${formattedTime}.csv`;
            
            saveCsv(csvData, { filename });
        }).catch((error) => {
            this.showError(error);
        });
    };

    onDatePickerDateRangeChanged = (
        datePickerDateRange: CCDatePickerDateRange
    ) => {
        const {
            rootStore: {
                managerStore: { updateReportFilter },
            },
        } = this.props;
        const {from: start, to: end, buttonType} = datePickerDateRange;
        const startDate = !buttonType || !start ? undefined : dayjs(start).format(DATEFORMAT);
        const endDate = !buttonType || !end ? undefined : dayjs(end).format(DATEFORMAT);

        updateReportFilter({ datePickerDateRange });
        this.setState({
            startDate,
            endDate
        }, () => {
            this.updatePCNList();
        });
    };

    deleteCurrentAssignment = (secondaryId: string) => {
        const {
            rootStore: {
                managerStore
            },
            userTeam
        } = this.props;

        managerStore.removeUserAssignment(secondaryId, userTeam).then((response) => {
            this.showSnackbar(
                response,
                'success' as CCSnackbarVariant
            );

            this.onCancelWizard();
            this.updatePCNList();
        }).catch((error) => {
            this.showError(error);
        });
    };

    getFilterOptionSelected = (option: FolderFilter, value: FolderFilter) => {
        return option.name === value.name;
    };

    public render() {
        const {
            isAssignmentsDrawerOpen,
            snackbarVariant,
            snackbarMessage,
            snackbarOpen,
            dialogOpen,
            dialogTitle,
            dialogMessage,
            isAssignmentWizardOpen,
            currentFolder,
            selectedPcn,
            pcnUsers,
            isFilterLoading,
            pcnFilters,
            folderFilters,
            filterPcnValue,
            filterFolderValue,
            filterEmailValue,
            pcnOffset,
            teamUsers,
            statusFilter,
            statusFilterValue,
            isFilterFolderOpen,
            isFilterPcnOpen,
            isFilterEmailOpen,
            sessionFilterValue,
            sessionTypes
        } = this.state;

        const {
            rootStore: {
                managerStore: {
                    isRoutesTabLoading,
                    pcnRoutesTotal,
                    pcnRoutes,
                    isPcnRoutesLoading,
                    isTeamUsersLoading,
                    reportFilters: {
                        datePickerDateRange
                    }
                }
            },
            classes
        } = this.props;

        const pcnItems =
            pcnRoutes ?
                this.convertPCNRoutesIntoListItems(pcnRoutes)
                : [];

        const { PCN_ROUTES_MAX_ROWS } =  PCN_ROUTES;
        const prefixName = CC_ROUTES_NAME ? CC_ROUTES_NAME : '';
        // Enable select datepicker days until year 2286
        const disableFrom = new Date(9999999999999);

        return (
            <>
                <SideNavbar
                    variant="persistent"
                    isDrawerOpen={isAssignmentsDrawerOpen}
                    collapsedArea={
                        <div
                            className={`${classes.navbarIconContainer} ${classes.navbarIconContainerSmall}`}
                        >
                            <ArticleIcon className={classes.navbarIcon} />
                        </div>
                    }
                    onExpanderClick={this.onToggleSideNavBar}
                >
                    <div className={`${classes.contentContainer} ${classes.navbarContent}`}>
                        <div className={classes.navbarTitleContainer}>
                            <div className={classes.navbarIconContainer}>
                                <ArticleIcon className={classes.navbarIcon} />
                            </div>
                            <Typography
                                className={classes.navbarTitle}
                                variant="subtitle1"
                            >
                                Assignments Filters
                            </Typography>
                        </div>
                        <Divider className={classes.divider} />
                        <div className={classes.inputContainer}>
                            <Typography
                                className={classes.navbarComponentTitle}
                                variant="button"
                            >
                                DATE RANGE
                            </Typography>
                            <CCSpinner
                                loading={isFilterLoading}
                                size={40}
                            >
                                <CCDatePickerButton
                                    dateRange={{...datePickerDateRange, disableFrom }}
                                    onDateRangeChange={
                                        this.onDatePickerDateRangeChanged
                                    }
                                />
                            </CCSpinner>
                        </div>
                        <div className={classes.inputContainer}>
                            <Typography
                                className={classes.navbarComponentTitle}
                                variant="button"
                            >
                                BUILDING
                            </Typography>
                            <Paper>
                                <CCSpinner
                                    loading={isFilterLoading}
                                    size={40}
                                >
                                    <Autocomplete
                                        className={classes.navbarInput}
                                        inputValue={filterFolderValue}
                                        onInputChange={this.setFilterFolderValue}
                                        open={isFilterFolderOpen}
                                        options={folderFilters}
                                        getOptionLabel={(option) => option.name}
                                        isOptionEqualToValue={this.getFilterOptionSelected}
                                        loading={isPcnRoutesLoading}
                                        disabled={isPcnRoutesLoading && filterFolderValue === ''}
                                        clearOnBlur={true}
                                        blurOnSelect={true}
                                        renderOption={(props, option) => {
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            const { key, ...optionProps } = props as any;
                                            const { id, name } = option;
                                            return (
                                                <li key={`${id}-${key}`} {...optionProps}>
                                                    {name}
                                                </li>
                                            );
                                        }}
                                        renderInput={(params) => 
                                            <TextField
                                                {...params}
                                                placeholder="Search for Building"
                                                variant="standard"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    disableUnderline: true,
                                                    classes: {
                                                        input: classes.navbarPlaceholder,
                                                    },
                                                }}
                                            />
                                        }
                                        onChange={(event, pickedValue) => this.onFolderFilterInputChange(event, pickedValue)}
                                    />
                                </CCSpinner>
                            </Paper>
                        </div>
                        <div className={classes.inputContainer}>
                            <Typography
                                className={classes.navbarComponentTitle}
                                variant="button"
                            >
                                { prefixName } ROUTE
                            </Typography>
                            <Paper>
                                <CCSpinner
                                    loading={isFilterLoading}
                                    size={40}
                                >
                                    <Autocomplete
                                        className={classes.navbarInput}
                                        inputValue={filterPcnValue}
                                        onInputChange={this.setFilterPcnValue}
                                        open={isFilterPcnOpen}
                                        options={pcnFilters}
                                        getOptionLabel={(option) => option.value}
                                        loading={isPcnRoutesLoading}
                                        disabled={isPcnRoutesLoading && filterPcnValue === ''}
                                        clearOnBlur={true}
                                        blurOnSelect={true}
                                        renderOption={(props, option) => {
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            const { key, ...optionProps } = props as any;
                                            const { id, folder_doc_id, value } = option;
                                            const optionKey = `${key}-${id}-${folder_doc_id}`;
                                            return (
                                                <li key={optionKey} {...optionProps}>
                                                    {value}
                                                </li>
                                            );
                                        }}
                                        renderInput={(params) => 
                                            <TextField
                                                {...params}
                                                placeholder="Search for Route"
                                                variant="standard"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    disableUnderline: true,
                                                    classes: {
                                                        input: classes.navbarPlaceholder,
                                                    },
                                                }}
                                            />
                                        }
                                        onChange={(event, pickedValue) => this.onPcnFilterInputChange(event, pickedValue)}
                                    />
                                </CCSpinner>
                            </Paper>
                        </div>
                        <div className={classes.inputContainer}>
                            <Typography
                                className={classes.navbarComponentTitle}
                                variant="button"
                            >
                                EMAIL
                            </Typography>
                            <Paper>
                                <CCSpinner
                                    loading={isFilterLoading}
                                    size={40}
                                >
                                    <Autocomplete
                                        className={classes.navbarInput}
                                        inputValue={filterEmailValue}
                                        onInputChange={this.setFilterEmailValue}
                                        open={isFilterEmailOpen}
                                        options={teamUsers}
                                        getOptionLabel={(option) => option.email}
                                        loading={isPcnRoutesLoading}
                                        disabled={isPcnRoutesLoading && filterEmailValue === ''}
                                        clearOnBlur={true}
                                        blurOnSelect={true}
                                        renderOption={(props, option) => {
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            const { key, ...optionProps } = props as any;
                                            const { user_id, email } = option;
                                            return (
                                                <li key={`${user_id}-${key}`} {...optionProps}>
                                                    {email}
                                                </li>
                                            );
                                        }}
                                        renderInput={(params) => 
                                            <TextField
                                                {...params}
                                                placeholder="Search for Email"
                                                variant="standard"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    disableUnderline: true,
                                                    classes: {
                                                        input: classes.navbarPlaceholder,
                                                    },
                                                }}
                                            />
                                        }
                                        onChange={(event, pickedValue) => this.onEmailFilterInputChange(event, pickedValue)}
                                    />
                                </CCSpinner>
                            </Paper>
                        </div>
                        <div className={classes.inputContainer}>
                            <Typography
                                className={classes.navbarComponentTitle}
                                variant="button"
                            >
                                ASSIGNMENT STATUS
                            </Typography>
                            <Paper>
                                <CCSpinner
                                    loading={isFilterLoading}
                                    size={40}
                                >
                                    <Autocomplete
                                        className={classes.navbarInput}
                                        inputValue={statusFilterValue}
                                        onInputChange={this.setFilterStatusValue}
                                        options={ASSIGNMENT_STATUS_FILTER}
                                        getOptionLabel={(option) => option.label}
                                        disabled={isPcnRoutesLoading && statusFilterValue === ''}
                                        clearOnBlur={true}
                                        blurOnSelect={true}
                                        renderOption={(props, option) => {
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            const { key, ...optionProps } = props as any;
                                            const { label, value } = option;
                                            const optionKey = `${key}-${label}`;
                                            return (
                                                <li key={optionKey} {...optionProps}>
                                                    {value}
                                                </li>
                                            );
                                        }}
                                        renderInput={(params) => 
                                            <TextField
                                                {...params}
                                                placeholder="Filter by status"
                                                variant="standard"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    disableUnderline: true,
                                                    classes: {
                                                        input: classes.navbarPlaceholder,
                                                    },
                                                }}
                                            />
                                        }
                                        defaultValue={statusFilter}
                                        onChange={(event, pickedValue) => this.onStatusFilterInputChange(event, pickedValue)}
                                    />
                                </CCSpinner>
                            </Paper>
                        </div>
                        {
                            this.isNewVersion && (
                                <div className={classes.inputContainer}>
                                    <Typography
                                        className={classes.navbarComponentTitle}
                                        variant="button"
                                    >
                                        SESSION TYPE
                                    </Typography>
                                    <Paper>
                                        <CCSpinner
                                            loading={isFilterLoading}
                                            size={40}
                                        >
                                            <Autocomplete
                                                className={classes.navbarInput}
                                                inputValue={sessionFilterValue}
                                                onInputChange={this.setFilterSessionValue}
                                                options={sessionTypes}
                                                getOptionLabel={(option) => option.name}
                                                disabled={isPcnRoutesLoading && sessionFilterValue === ''}
                                                clearOnBlur={true}
                                                blurOnSelect={true}
                                                renderOption={(props, option) => {
                                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                    const { key, ...optionProps } = props as any;
                                                    const { id, name } = option;
                                                    return (
                                                        <li key={`${id}-${key}`} {...optionProps}>
                                                            {name}
                                                        </li>
                                                    );
                                                }}
                                                renderInput={(params) => 
                                                    <TextField
                                                        {...params}
                                                        placeholder="Filter by Session type"
                                                        variant="standard"
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            disableUnderline: true,
                                                            classes: {
                                                                input: classes.navbarPlaceholder,
                                                            },
                                                        }}
                                                    />
                                                }
                                                onChange={(event, pickedValue) => this.onSessionTypeFilterInputChange(event, pickedValue)}
                                            />
                                        </CCSpinner>
                                    </Paper>
                                </div>
                            )

                        }
                        <Divider className={classes.divider} />
                        <Button
                            className={classes.routeNavButton}
                            onClick={this.exportToCSV}
                            variant="contained"
                            color="secondary"
                            size="large"
                        >
                            <Print /> Export to CSV
                        </Button>
                    </div>
                </SideNavbar>
                {
                    !selectedPcn ? '' :
                        <AssignRouteWizard
                            onSubmit={this.onWizardSubmit}
                            users={pcnUsers}
                            listUsers={teamUsers}
                            selectedFolder={currentFolder}
                            selectedPcn={selectedPcn}
                            open={isAssignmentWizardOpen}
                            onCancelClick={this.onCancelWizard}
                            onFilterUserByEmail={this.onFilterUserByEmail}
                            onDeleteUserAssignment={this.deleteCurrentAssignment}
                            isLoading={isTeamUsersLoading}
                        />
                }
                
                <div className={classes.assignmentsContainer}>
                    <Typography
                        className={classes.bodyTitle}
                        variant="h5"
                    >
                        Select a { prefixName } Route from the list
                    </Typography>
                    <div className={classes.pcnList}>
                        <SimpleListPagination
                            items={pcnItems}
                            noItemsLabel="Empty Results"
                            onListItemClick={this.selectPCN}
                            rowsPerPage={PCN_ROUTES_MAX_ROWS}
                            onPageOverBoundary={this.onPCNBufferOverBoundary}
                            totalItems={pcnRoutesTotal}
                            offset={pcnOffset}
                        />
                    </div>
                </div>
                <CCSnackbar
                    className={classes.snackbar}
                    variant={snackbarVariant}
                    message={snackbarMessage}
                    autoHideDuration={4000}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    transition="right"
                    open={snackbarOpen}
                    onClose={this.onSnackbarClosed}
                />
                <SimpleDialog
                    open={dialogOpen}
                    titleText={dialogTitle}
                    buttonCancelLabel=""
                    contentText={dialogMessage}
                    onDialogResult={this.onDialogClosed}
                    disableTransition={true}
                />
                <CCSpinner
                    className={classes.spinner}
                    loading={isRoutesTabLoading || isPcnRoutesLoading}
                    overlayVisible={true}
                    size={100}
                />
            </>
        );
    }

}

const MUIComponent = withStyles(styles)(AssignmentsTab);
export { MUIComponent as AssignmentsTab };
